import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { 
  FileText, 
  RefreshCw, 
  Edit, 
  Save, 
  X, 
  Plus, 
  Download,
  Clock,
  FileCode,
  BookOpen,
  Server,
  Database,
  Layout,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemDocumentationPage = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [activeTab, setActiveTab] = useState('view');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/documentation`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch documents');
      
      const data = await response.json();
      setDocuments(data.documents || []);
      
      // Auto-select first document
      if (data.documents?.length > 0 && !selectedDoc) {
        loadDocument(data.documents[0].id);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const loadDocument = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/documentation/${docId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch document');
      
      const data = await response.json();
      setSelectedDoc(data);
      setDocContent(data.content);
      setEditContent(data.content);
      setEditing(false);
    } catch (error) {
      console.error('Error loading document:', error);
      toast.error('Failed to load document');
    }
  };

  const saveDocument = async () => {
    if (!selectedDoc) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/documentation/${selectedDoc.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: editContent })
      });
      
      if (!response.ok) throw new Error('Failed to save document');
      
      setDocContent(editContent);
      setSelectedDoc({ ...selectedDoc, content: editContent });
      setEditing(false);
      toast.success('Documentation saved');
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    }
  };

  const createDocument = async () => {
    if (!newDocTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/documentation`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          title: newDocTitle,
          content: `# ${newDocTitle}\n\nAdd documentation content here...`
        })
      });
      
      if (!response.ok) throw new Error('Failed to create document');
      
      const data = await response.json();
      toast.success('Document created');
      setShowCreateDialog(false);
      setNewDocTitle('');
      fetchDocuments();
      loadDocument(data.id);
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error('Failed to create document');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(docContent);
    toast.success('Copied to clipboard');
  };

  const downloadDocument = () => {
    if (!selectedDoc) return;
    
    const blob = new Blob([docContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedDoc.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Document downloaded');
  };

  const getDocIcon = (docId) => {
    if (docId?.includes('architecture')) return <Server className="h-5 w-5 text-blue-500" />;
    if (docId?.includes('audit')) return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    if (docId?.includes('prd')) return <FileCode className="h-5 w-5 text-purple-500" />;
    if (docId?.includes('changelog')) return <Clock className="h-5 w-5 text-green-500" />;
    if (docId?.includes('roadmap')) return <Layout className="h-5 w-5 text-indigo-500" />;
    return <FileText className="h-5 w-5 text-slate-500" />;
  };

  return (
    <div className="p-6 space-y-6" data-testid="system-documentation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-indigo-600" />
            System Documentation
          </h1>
          <p className="text-muted-foreground mt-1">
            Architecture guides, audit reports, and build documentation
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDocuments}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Document
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Document List Sidebar */}
        <div className="col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-5 w-5" />
                Documents
              </CardTitle>
              <CardDescription>
                {documents.length} documentation files
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No documentation files</p>
                  <Button 
                    variant="link" 
                    onClick={() => setShowCreateDialog(true)}
                    className="mt-2"
                  >
                    Create your first document
                  </Button>
                </div>
              ) : (
                documents.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => loadDocument(doc.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedDoc?.id === doc.id 
                        ? 'bg-indigo-50 border border-indigo-200' 
                        : 'hover:bg-slate-50 border border-transparent'
                    }`}
                    data-testid={`doc-item-${doc.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {getDocIcon(doc.id)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{doc.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(doc.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Document Viewer/Editor */}
        <div className="col-span-9">
          {selectedDoc ? (
            <Card className="h-full">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getDocIcon(selectedDoc.id)}
                    <div>
                      <CardTitle>{selectedDoc.title}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3" />
                        Last modified: {selectedDoc.modified ? format(new Date(selectedDoc.modified), 'PPp') : 'Unknown'}
                        <Badge variant="outline" className="ml-2">
                          {selectedDoc.filename}
                        </Badge>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadDocument}>
                      <Download className="h-4 w-4" />
                    </Button>
                    {editing ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={saveDocument}>
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                {/* View/Edit Tabs */}
                {editing && (
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                    <TabsList>
                      <TabsTrigger value="edit">Edit</TabsTrigger>
                      <TabsTrigger value="preview">Preview</TabsTrigger>
                      <TabsTrigger value="split">Split View</TabsTrigger>
                    </TabsList>
                  </Tabs>
                )}
              </CardHeader>
              
              <CardContent className="p-0">
                {editing ? (
                  <div className="h-[calc(100vh-320px)]">
                    {activeTab === 'edit' && (
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="h-full w-full rounded-none border-0 resize-none font-mono text-sm p-4"
                        placeholder="Enter markdown content..."
                      />
                    )}
                    {activeTab === 'preview' && (
                      <div className="h-full overflow-auto p-6 prose prose-sm max-w-none">
                        <ReactMarkdown>{editContent}</ReactMarkdown>
                      </div>
                    )}
                    {activeTab === 'split' && (
                      <div className="grid grid-cols-2 h-full divide-x">
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="h-full rounded-none border-0 resize-none font-mono text-sm p-4"
                          placeholder="Enter markdown content..."
                        />
                        <div className="h-full overflow-auto p-6 prose prose-sm max-w-none bg-slate-50">
                          <ReactMarkdown>{editContent}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-[calc(100vh-280px)] overflow-auto p-6 prose prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({node, ...props}) => <h1 className="text-2xl font-bold border-b pb-2 mb-4" {...props} />,
                        h2: ({node, ...props}) => <h2 className="text-xl font-semibold mt-6 mb-3" {...props} />,
                        h3: ({node, ...props}) => <h3 className="text-lg font-medium mt-4 mb-2" {...props} />,
                        table: ({node, ...props}) => <table className="min-w-full border-collapse border border-slate-300 my-4" {...props} />,
                        th: ({node, ...props}) => <th className="border border-slate-300 bg-slate-100 px-3 py-2 text-left font-medium" {...props} />,
                        td: ({node, ...props}) => <td className="border border-slate-300 px-3 py-2" {...props} />,
                        code: ({node, inline, ...props}) => inline 
                          ? <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm" {...props} />
                          : <code className="block bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm" {...props} />,
                        pre: ({node, ...props}) => <pre className="bg-slate-900 rounded-lg my-4 overflow-x-auto" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc pl-6 my-2" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal pl-6 my-2" {...props} />,
                        li: ({node, ...props}) => <li className="my-1" {...props} />,
                        blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-indigo-500 pl-4 italic my-4 text-slate-600" {...props} />,
                        hr: ({node, ...props}) => <hr className="my-6 border-slate-200" {...props} />
                      }}
                    >
                      {docContent}
                    </ReactMarkdown>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full flex items-center justify-center">
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">Select a Document</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a document from the sidebar to view its contents
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Create Document Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Document</DialogTitle>
            <DialogDescription>
              Create a new documentation file. The file will be saved as a Markdown (.md) file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="doc-title">Document Title</Label>
              <Input
                id="doc-title"
                value={newDocTitle}
                onChange={(e) => setNewDocTitle(e.target.value)}
                placeholder="e.g., API Reference, Deployment Guide"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will create: {newDocTitle ? `${newDocTitle.toUpperCase().replace(/\s+/g, '_')}.md` : 'NEW_DOCUMENT.md'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createDocument}>
              <Plus className="h-4 w-4 mr-1" />
              Create Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemDocumentationPage;
